/**
 * The clip player full screen. The player's frame goes full screen, not its
 * <video>, so what the page draws over the clip (Up next and its Cancel) is
 * full screen too. An iPhone has the Fullscreen API only for a bare <video>
 * (its own player, which shows nothing of ours), so there the frame fills the
 * window instead: `.vid.full` is fixed over the page and `html.full-window`
 * stops the page scrolling under it.
 */

let filled: HTMLElement | null = null;
const subs = new Set<() => void>();

/** The element that is full screen or fills the window, if any. */
export function fullElement(): Element | null {
  return document.fullscreenElement ?? filled;
}

/** Calls `f` whenever an element goes full screen or leaves it; returns the unsubscribe. */
export function onFullChange(f: () => void): () => void {
  subs.add(f);
  document.addEventListener("fullscreenchange", f);
  return () => {
    subs.delete(f);
    document.removeEventListener("fullscreenchange", f);
  };
}

/**
 * Takes `frame` full screen. A landscape clip also turns a phone to landscape,
 * as the browser's own video full screen does; the browser ends that lock with
 * the full screen, and refuses it on a desktop.
 */
export function enterFull(frame: HTMLElement, video: HTMLVideoElement | null): void {
  if (!document.fullscreenEnabled || typeof frame.requestFullscreen !== "function") return fill(frame);
  frame.requestFullscreen({ navigationUI: "hide" }).then(
    () => {
      if (video && video.videoWidth > video.videoHeight) (screen.orientation as Lockable | undefined)?.lock?.("landscape").catch(() => {});
    },
    () => fill(frame),
  );
}

export function exitFull(): void {
  if (filled) {
    filled = null;
    document.documentElement.classList.remove("full-window");
    window.removeEventListener("keydown", onEscape);
    subs.forEach((f) => f());
  } else if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => {});
  }
}

function fill(frame: HTMLElement): void {
  filled = frame;
  document.documentElement.classList.add("full-window");
  window.addEventListener("keydown", onEscape);
  subs.forEach((f) => f());
}

/** Esc leaves the filled window as it leaves a real full screen. */
function onEscape(e: KeyboardEvent): void {
  if (e.key === "Escape") exitFull();
}

/** TypeScript's DOM types lack `lock`; Android browsers have it. */
type Lockable = ScreenOrientation & { lock?: (orientation: "landscape") => Promise<void> };
